import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import FileProp from '../propTypes/fileProp';

const WORKER_SRC = '/dist/pdf.worker.min.mjs';

/**
 * In-house (Quill) "sign anywhere" tool for the journey SIGNATURE step — replaces
 * the PSPDFKit signature flow. Lazy-loads `PdfSigner` (client-only, so pdf.js
 * isn't evaluated during SSR). The signer clicks Sign, draws a signature and
 * drops it anywhere, repeatable across pages; on submit the placements are
 * flattened into the PDF.
 *
 * Exposes the same ref the shared `handleExportPDF` flow expects:
 *  - `exportFormFields()` / `checkForSignatureField()` — no-ops (no form fields).
 *  - `exportPDF()` → `{ formData, ext, size }` (FormData with the signed PDF as
 *    `file`), identical to the field-based path so the upload is unchanged.
 */
const QuillSigner = forwardRef(({ documentFile, height }, ref) => {
  const [Signer, setSigner] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const signerRef = useRef(null);

  useEffect(() => {
    let alive = true;
    import('@bravon-io/quill-filler')
      .then(mod => {
        if (alive) setSigner(() => mod.PdfSigner);
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Failed to load Quill signer', err);
        if (alive) setLoadError(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      // Free-form signing has no form fields — keep the shared export flow happy.
      exportFormFields: async () => ({ formFieldValues: [], formFields: [] }),
      checkForSignatureField: async () => false,
      exportPDF: async () => {
        if (!signerRef.current) return null;
        const file = await signerRef.current.exportPDF();
        if (!file) {
          // Nothing placed — block the upload (handleExportPDF surfaces the error).
          throw new Error('Add at least one signature before submitting.');
        }
        const formData = new FormData();
        formData.append('file', file);
        return { formData, ext: '.pdf', size: file.size };
      },
    }),
    [],
  );

  if (loadError) {
    return (
      <div style={{ padding: 24, color: '#e03131' }}>
        Failed to load the signing tool.
      </div>
    );
  }

  if (!Signer) {
    return <div style={{ padding: 24, color: '#868e96' }}>Loading…</div>;
  }

  return (
    <Signer
      ref={signerRef}
      documentUrl={documentFile && documentFile.url}
      workerSrc={WORKER_SRC}
      height={height}
    />
  );
});

QuillSigner.displayName = 'QuillSigner';

QuillSigner.propTypes = {
  documentFile: PropTypes.shape(FileProp.propType),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

QuillSigner.defaultProps = {
  documentFile: null,
  height: '75vh',
};

export default QuillSigner;
